/**
 * `fg.tour.v1` — the guided-tour content model.
 *
 * A tour is a short, declarative walkthrough that drives the *real* app. Unlike
 * a capture-and-replay demo (Storylane, Navattic, Walnut), nothing here is a
 * screenshot: a step names a route, an anchor in the live DOM, and one guide
 * widget to render beside it. The product does the rest.
 *
 * That choice buys live drill-through and real numbers, and costs exactly one
 * thing: **anchor fragility**. A frozen screen can never break; a live-app tour
 * breaks the moment someone renames a button. Every design decision in this
 * file exists to make that failure loud, cheap and non-fatal — see `TourAnchor`
 * (ordered candidates) and `TourStepFallback` (no step may hard-fail in front
 * of a prospect).
 *
 * The same document renders on five surfaces (`TourContext`): the public demo,
 * an embedded marketing iframe, first-login onboarding, a firm's own
 * client workspace, and the firm cockpit itself. Keep it serializable —
 * it round-trips through Postgres
 * (`fg_tour_versions.doc`) and through serde on the server.
 */

import type { CfoPageSlug } from '../parity-dev/cfo/cfoRoutes'

export const TOUR_SCHEMA_VERSION = 'fg.tour.v1'

/** Which surface a tour is authored for. Gates route and widget validation. */
export type TourContext = 'demo' | 'embed' | 'onboarding' | 'client' | 'firm'

/** Sample-company personas the demo entry already understands. */
export type TourIndustry = 'hvac' | 'restaurant' | 'law' | 'saas' | 'distributor'

// ---------------------------------------------------------------------------
// Anchors
// ---------------------------------------------------------------------------

/**
 * How a step finds its element. Resolution tries the descriptor's own selector
 * first and the resolver reports *which* kind matched, so a silent slide from
 * `tour` down to `testid` is visible in telemetry before the testid is renamed
 * out from under us.
 *
 * `kind: 'css'` is the authoring escape hatch and is always `brittle`. The
 * validator refuses to publish one without a recorded override.
 */
export type TourAnchorKind = 'tour' | 'testid' | 'segment' | 'surface' | 'nav' | 'text' | 'css'

export type TourAnchorBase = {
  /** Human label shown in the editor and stamped onto `tour.anchor_missed`. */
  label: string
  /** Restrict the search to inside another anchor. */
  within?: TourAnchor | null
  /** 0-based index when the selector legitimately matches several elements. */
  nth?: number | null
  /** Resolution budget before `fallback` applies. Default `DEFAULT_ANCHOR_TIMEOUT_MS`. */
  timeoutMs?: number | null
}

export type TourAnchor =
  | (TourAnchorBase & { kind: 'tour'; value: string })
  | (TourAnchorBase & { kind: 'testid'; value: string })
  | (TourAnchorBase & { kind: 'segment'; control: string; value: string })
  | (TourAnchorBase & { kind: 'surface'; value: string })
  | (TourAnchorBase & { kind: 'nav'; value: CfoPageSlug })
  | (TourAnchorBase & { kind: 'text'; role: string; name: string })
  | (TourAnchorBase & { kind: 'css'; value: string; brittle: true })

export const DEFAULT_ANCHOR_TIMEOUT_MS = 8000

/**
 * Anchor stability, surfaced in the authoring picker and enforced at publish.
 * `A` is contract-grade, `B` is borrowed from tests, `D` is a derived CSS path
 * that will break.
 */
export type TourAnchorGrade = 'A' | 'B' | 'D'

export const TOUR_ANCHOR_GRADE: Record<TourAnchorKind, TourAnchorGrade> = {
  tour: 'A',
  segment: 'A',
  testid: 'B',
  surface: 'B',
  nav: 'B',
  text: 'B',
  css: 'D',
}

// ---------------------------------------------------------------------------
// Widgets
// ---------------------------------------------------------------------------

export type TourWidgetKind =
  | 'tooltip'
  | 'hotspot'
  | 'text_modal'
  | 'media_modal'
  | 'lead_form'
  | 'their_numbers'
  | 'none'

export type TourSide = 'top' | 'right' | 'bottom' | 'left'
export type TourAlign = 'start' | 'center' | 'end'

export type TourHighlight = {
  /** Dim everything outside the anchor. */
  backdrop: boolean
  /** Draw the focus ring around the anchor. */
  spotlight: boolean
  paddingPx: number
  radiusPx: number
}

export type TourPosition = {
  side: TourSide | 'auto'
  align: TourAlign
  arrow: boolean
}

export type TourWidgetConfig = {
  showStepNumber: boolean
  showPrev: boolean
  dismissible: boolean
}

export type TourCtaAction =
  | { kind: 'next' }
  | { kind: 'prev' }
  | { kind: 'end' }
  | { kind: 'goto_step'; stepId: string }
  | { kind: 'external_url'; url: string; newTab: boolean }
  | { kind: 'signup'; plan?: string | null }

export type TourCta = {
  label: string
  action: TourCtaAction
  variant: 'primary' | 'secondary'
}

export type TourMedia = {
  src: string
  alt: string
  poster?: string | null
  kind: 'image' | 'video'
}

export type TourWidget =
  | { kind: 'tooltip'; title?: string | null; body: string; highlight?: TourHighlight | null; position?: TourPosition | null; ctas?: TourCta[] | null; config?: TourWidgetConfig | null }
  | { kind: 'hotspot'; body?: string | null; highlight?: TourHighlight | null; position?: TourPosition | null; config?: TourWidgetConfig | null }
  | { kind: 'text_modal'; title: string; body: string; ctas?: TourCta[] | null; config?: TourWidgetConfig | null }
  | { kind: 'media_modal'; title?: string | null; body?: string | null; media: TourMedia; ctas?: TourCta[] | null; config?: TourWidgetConfig | null }
  | { kind: 'lead_form'; title: string; body: string; submitLabel?: string | null }
  | { kind: 'their_numbers'; title: string; body?: string | null; submitLabel?: string | null }
  | { kind: 'none' }

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

/** Where a step points the app before it renders. */
export type TourRoute = {
  /** CFO page slug — or, in a context:'client' doc, a workspace page id.
   *  Typed as the CFO union plus string so client docs parse; the validator
   *  applies the right rule per context. */
  page: CfoPageSlug | (string & {})
  /** Extra query params, e.g. `fg_focus_metric`. */
  params?: Record<string, string> | null
  /** Click a segmented control once the page is up (`data-cfo-segment*`). */
  segment?: { control: string; value: string } | null
}

/** What moves the tour forward. */
export type TourAdvance =
  | { kind: 'cta' }
  | { kind: 'click_anchor' }
  | { kind: 'route_change'; to: TourRoute }
  | { kind: 'element_appears'; anchor: TourAnchor }
  | { kind: 'timeout'; ms: number }

/**
 * What to do when an anchor never resolves. There is deliberately no "fail"
 * option — a broken overlay in front of a prospect is the worst outcome this
 * system can produce.
 */
export type TourStepFallback = 'modal' | 'skip' | 'block'

/** Projection of a step onto the Excel/MCP agent surface (`fg.cfo_guided_tour.v1`). */
export type TourExcelProjection = {
  preferredSheet: string
  preferredModuleId?: string | null
  focusRange: string
  objective: string
  demonstrate: string[]
}

export type TourStep = {
  id: string
  title: string
  route: TourRoute
  /** `null` renders a centered modal with no anchor. */
  anchor: TourAnchor | null
  widget: TourWidget
  advance: TourAdvance
  fallback: TourStepFallback
  surfaces?: { excel?: TourExcelProjection | null } | null
  /** Spoken narration. `script` overrides the widget body as the TTS text;
   *  omitted → the body (tokens rendered with their defaults) is spoken;
   *  `{ script: null }` explicitly silences a step that has a body. */
  voice?: { script?: string | null } | null
}

export type TourChapter = {
  id: string
  title: string
  steps: TourStep[]
}

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

export type TourTokenKind = 'text' | 'image' | 'date' | 'fact'

/**
 * A declared personalization slot. `defaultValue` is required by the validator:
 * a prospect seeing a literal `{{first_name}}` is a worse outcome than a
 * generic greeting, so tokens always render *something*.
 */
export type TourTokenDecl = {
  key: string
  kind: TourTokenKind
  defaultValue: string
  label?: string | null
}

export type TourAudience = Record<string, string>

// ---------------------------------------------------------------------------
// Tour
// ---------------------------------------------------------------------------

export type TourEntry = {
  persona?: 'owner' | 'firm' | null
  company?: TourIndustry | null
  route: TourRoute
}

export type TourChecklistItem = { id: string; label: string; stepId: string }

export type TourDoc = {
  schemaVersion: typeof TOUR_SCHEMA_VERSION
  slug: string
  name: string
  description?: string | null
  context: TourContext
  entry: TourEntry
  chapters: TourChapter[]
  /** Always-visible conversion button, Storylane's "Convert CTA". */
  convertCta?: TourCta | null
  checklist?: { title: string; items: TourChecklistItem[] } | null
  tokens?: TourTokenDecl[] | null
  /** Recorded override that lets a `css` anchor reach `published`. */
  allowBrittleAnchors?: boolean | null
  /** Visual accent for the widgets; a hex color replacing the default blue. */
  theme?: { accent?: string | null; voiceId?: string | null } | null
}

/** Where a loaded doc came from — stamped on every event so drift is visible. */
export type TourSource = 'db' | 'bundled'

export type LoadedTour = {
  doc: TourDoc
  source: TourSource
  /** Monotonic version from `fg_tour_versions`; `0` for bundled JSON. */
  version: number
}

// ---------------------------------------------------------------------------
// Flattened runtime view
// ---------------------------------------------------------------------------

/** A step plus its resolved position in the flattened tour. */
export type TourStepRef = {
  step: TourStep
  chapter: TourChapter
  chapterIndex: number
  /** 0-based index across the whole tour. */
  index: number
}

/** Flatten chapters into the ordered step list the engine walks. */
export function flattenTourSteps(doc: TourDoc): TourStepRef[] {
  const out: TourStepRef[] = []
  doc.chapters.forEach((chapter, chapterIndex) => {
    chapter.steps.forEach((step) => {
      out.push({ step, chapter, chapterIndex, index: out.length })
    })
  })
  return out
}

export const tourStepCount = (doc: TourDoc): number =>
  doc.chapters.reduce((sum, chapter) => sum + chapter.steps.length, 0)

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_HIGHLIGHT: TourHighlight = {
  backdrop: true,
  spotlight: true,
  paddingPx: 8,
  radiusPx: 10,
}

export const DEFAULT_POSITION: TourPosition = { side: 'auto', align: 'center', arrow: true }

export const DEFAULT_WIDGET_CONFIG: TourWidgetConfig = {
  showStepNumber: true,
  showPrev: true,
  dismissible: true,
}
